import { useCallback, useState } from 'react';

import * as amplitude from '@amplitude/analytics-browser';

import { LinkPage, handleLeaveLink } from '../components/ButtonExitLink';
import { useJWT } from '../contexts/JWTProvider';
import { SkipFeedbackMeta, SkipFeedbackPayload } from '../utils/skipFeedback';
import { SlackTracker, createSlackContext } from '../utils/slackNotifications';

interface UseSkipOnboardingProps {
  token: string;
  redirectURIError?: string;
  redirectURISuccess?: string;
  serviceName?: string;
  tsp: string;
}

/**
 * A hook for handling skip functionality in the onboarding process
 */
const useSkipOnboarding = ({
  token,
  redirectURIError,
  redirectURISuccess,
  serviceName,
  tsp,
}: UseSkipOnboardingProps) => {
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const jwt = useJWT();

  // Handle opening skip confirmation dialog
  const handleSkipClick = useCallback(() => {
    amplitude.track('User Initiated Skip Dialog', {
      tsp,
      serviceName,
      page: 'Onboard',
    });

    // Track skip initiation in Slack
    const slackContext = createSlackContext(jwt, serviceName, { tsp });
    SlackTracker.skipInitiated(slackContext, 'Onboard');

    setSkipDialogOpen(true);
  }, [tsp, serviceName, jwt]);

  // Handle actual skip action after confirmation
  const handleSkipConfirmed = useCallback(
    async (feedback?: SkipFeedbackPayload, meta?: SkipFeedbackMeta) => {
      amplitude.track('User Confirmed Skip Onboarding', {
        tsp,
        serviceName,
        redirectURL: redirectURIError,
      });

      setSkipDialogOpen(false);

      // Use the analytics system to track ERROR_QUIT event and handle redirect
      // This will trigger the failure webhook AND handle the redirect
      try {
        const { Analytics, createAnalyticsContext } = await import(
          '../utils/analytics'
        );

        const skipReasonParam = meta?.reasonLabel?.trim()
          ? meta.reasonLabel?.trim()
          : meta?.reasonValue || undefined;
        const skipTspParam = feedback?.tsp_name?.trim() || undefined;

        // Create analytics context
        const analyticsContext = createAnalyticsContext(jwt, serviceName, {
          tsp,
          redirectURIError,
          redirectURISuccess,
          skipReasonParam,
          skipTspParam,
          // Don't override token - let it use jwt.rawToken
        });

        // Use the confirmed skip function which handles both webhook and redirect
        await Analytics.confirmedSkip.errorQuit(
          analyticsContext,
          'user_quit', // errorType
          'Onboard' // page
        );
      } catch (error) {
        // Fallback to direct redirect if analytics fails
        console.warn(
          '[SkipOnboarding] Analytics failed, falling back to direct redirect:',
          error
        );
        const skipReasonParam = meta?.reasonLabel?.trim()
          ? meta.reasonLabel?.trim()
          : meta?.reasonValue || undefined;
        const skipTspParam = feedback?.tsp_name?.trim() || undefined;

        handleLeaveLink({
          token,
          redirectURIError: redirectURIError || '/error',
          serviceName: serviceName || '',
          pageOnExit: LinkPage.OnboardingCreds,
          skipReasonParam,
          skipTspParam,
        });
      }
    },
    [token, redirectURIError, serviceName, tsp, redirectURISuccess, jwt]
  );

  // Handle cancel of skip dialog
  const handleCloseSkipDialog = useCallback(() => {
    amplitude.track('User Canceled Skip Dialog', {
      serviceName,
      page: 'Onboard',
    });

    setSkipDialogOpen(false);
  }, [serviceName]);

  return {
    skipDialogOpen,
    handleSkipClick,
    handleSkipConfirmed,
    handleCloseSkipDialog,
  };
};

export default useSkipOnboarding;
